// Report Concern Service for Pops Culture
import { db } from './firebase';
import { collection, doc, setDoc, updateDoc } from 'firebase/firestore';
import type { ConcernCategory, ConcernReport } from '../types';
import { getCurrentProfile } from './authService';

export interface ReportConcernInput {
  type: 'user' | 'event';
  targetId: string;
  targetNameOrTitle: string;
  category: ConcernCategory;
  categoryLabel: string;
  message?: string;
}

/**
 * Report a concern regarding a user or event
 * - If user: immediately suspends their account with "under review" status
 * - If event: immediately unlists event as "under review"
 * - Logs report in Firestore 'reports' collection
 * - Triggers notification dispatch to support@popsculture.dad
 */
export async function submitConcernReport(input: ReportConcernInput): Promise<ConcernReport> {
  const current = getCurrentProfile();
  if (!current || !current.uid) {
    throw new Error('Must be signed in to submit a concern.');
  }

  const reportsCol = collection(db, 'reports');
  const reportDoc = doc(reportsCol);
  const now = Date.now();

  const report: ConcernReport = {
    id: reportDoc.id,
    type: input.type,
    targetId: input.targetId,
    targetNameOrTitle: input.targetNameOrTitle,
    reporterUid: current.uid,
    reporterEmail: current.email,
    category: input.category,
    categoryLabel: input.categoryLabel,
    message: input.message?.trim() || '',
    status: 'pending',
    createdAt: now
  };

  // Save report to Firestore
  await setDoc(reportDoc, report);

  // Take immediate enforcement action
  if (input.type === 'user') {
    // Immediately suspend user account
    const userRef = doc(db, 'users', input.targetId);
    try {
      await updateDoc(userRef, {
        status: 'suspended',
        underReview: true,
        suspensionReason: input.categoryLabel,
        suspendedAt: now,
        reportedBy: current.uid
      });
    } catch (err) {
      console.warn('[Pops Culture Report] Failed to suspend user doc:', err);
    }
  } else if (input.type === 'event') {
    // Immediately unlist event
    const missionRef = doc(db, 'missions', input.targetId);
    try {
      await updateDoc(missionRef, {
        status: 'under_review',
        reportedReason: input.categoryLabel,
        reportedBy: current.uid,
        reportedAt: now
      });
    } catch (err) {
      console.warn('[Pops Culture Report] Failed to unlist event doc:', err);
    }
  }

  // Dispatch email notification to support@popsculture.dad (via Firestore mail queue)
  try {
    const mailCol = collection(db, 'mail');
    const mailDoc = doc(mailCol);
    await setDoc(mailDoc, {
      to: ['support@popsculture.dad'],
      message: {
        subject: `[Pops Culture Flag] Review Requested: ${input.type.toUpperCase()} (${input.targetId})`,
        text: `Concern Reported:
Type: ${input.type.toUpperCase()}
Target ID: ${input.targetId} (${input.targetNameOrTitle})
Reporting User ID: ${current.uid} (${current.email})
Category: ${input.categoryLabel}
Message: ${input.message || 'None'}
Status: Review triggered. Immediate action taken.`,
        html: `
          <div style="font-family: sans-serif; padding: 16px; color: #111;">
            <h2>Pops Culture Concern Report &bull; Urgent Admin Review</h2>
            <p><strong>Item Type:</strong> ${input.type.toUpperCase()}</p>
            <p><strong>Target ID:</strong> <code>${input.targetId}</code> (<em>${input.targetNameOrTitle}</em>)</p>
            <p><strong>Reported By User:</strong> <code>${current.uid}</code> (${current.email})</p>
            <p><strong>Concern Category:</strong> <span style="color: #d9534f; font-weight: bold;">${input.categoryLabel}</span></p>
            <p><strong>Details / Message:</strong></p>
            <blockquote style="background: #f7f7f7; padding: 10px; border-left: 4px solid #d9534f;">
              ${input.message ? input.message.replace(/\n/g, '<br/>') : '<em>No additional message provided.</em>'}
            </blockquote>
            <p><strong>Immediate System Action:</strong> ${input.type === 'user' ? 'Target account immediately suspended (under review)' : 'Target event temporarily unlisted (under review)'}.</p>
            <hr/>
            <p><small>Review and take action via the Pops Culture Admin Console at /admin</small></p>
          </div>
        `
      },
      createdAt: now
    });
  } catch (err) {
    console.warn('[Pops Culture Report] Note: mail document queued or logged:', err);
  }

  console.log(`[Pops Culture Report] Flag triggered for ${input.type} ${input.targetId}. Admin review email sent to support@popsculture.dad`);
  return report;
}
